import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, Heart, Share2, Flag, User, MapPin, Calendar, Phone, Mail } from 'react-feather';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import { fundraiserService } from '../services/api';

const FundraiserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fundraiser, setFundraiser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    fetchFundraiser();
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchFundraiser = async () => {
    try {
      setLoading(true);
      const response = await fundraiserService.getById(id);
      setFundraiser(response.data);
    } catch (error) {
      console.error('Error fetching fundraiser:', error);
      setError('Fundraiser not found');
    } finally {
      setLoading(false);
    }
  };

  const handleDonate = () => {
    if (!fundraiser) return;
    
    const message = `Hi, I would like to donate to the fundraiser: "${fundraiser.title}" for ${fundraiser.patientName}. Please provide me with the donation details.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = fundraiser.whatsappNumber.replace(/\D/g, ''); // Remove non-digits
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: fundraiser.title,
        text: `Help ${fundraiser.patientName} by donating to this fundraiser`,
        url: window.location.href,
      });
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading fundraiser details...</p>
          </div>
        </div>
        <Footer />
      <FloatingChatButton />
      </>
    );
  }

  if (error || !fundraiser) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Fundraiser Not Found</h2>
            <p className="text-gray-600 mb-6">The fundraiser you're looking for doesn't exist or has been removed.</p>
            <button 
              onClick={() => navigate('/all-fundraisers')}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Browse All Fundraisers
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const allImages = [fundraiser.mainImage, ...(fundraiser.additionalImages || [])];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Image Gallery */}
              <div className="lg:col-span-2">
                <div className="mb-4">
                  <img
                    src={allImages[selectedImageIndex]}
                    alt={fundraiser.title}
                    className="w-full h-96 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
                    }}
                  />
                </div>
                
                {allImages.length > 1 && (
                  <div className="flex space-x-2 overflow-x-auto">
                    {allImages.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`View ${index + 1}`}
                        className={`w-20 h-20 object-cover rounded-md cursor-pointer flex-shrink-0 ${
                          selectedImageIndex === index ? 'ring-2 ring-blue-500' : ''
                        }`}
                        onClick={() => setSelectedImageIndex(index)}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Fundraiser Info */}
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getUrgencyColor(fundraiser.urgencyLevel)}`}>
                      {fundraiser.urgencyLevel?.charAt(0).toUpperCase() + fundraiser.urgencyLevel?.slice(1)} Priority
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleShare}
                        className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                        title="Share"
                      >
                        <Share2 size={20} />
                      </button>
                      <button
                        className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                        title="Report"
                      >
                        <Flag size={20} />
                      </button>
                    </div>
                  </div>

                  <h1 className="text-2xl font-bold text-gray-800 mb-4">{fundraiser.title}</h1>
                  
                  <div className="flex items-center space-x-2 text-gray-600 mb-4">
                    <User size={16} />
                    <span>by <strong>{fundraiser.patientName}</strong></span>
                  </div>

                  {fundraiser.treatmentLocation && (
                    <div className="flex items-center space-x-2 text-gray-600 mb-4">
                      <MapPin size={16} />
                      <span>
                        {[
                          fundraiser.treatmentLocation.hospital,
                          fundraiser.treatmentLocation.city,
                          fundraiser.treatmentLocation.state,
                          fundraiser.treatmentLocation.country
                        ].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Progress */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-2xl font-bold text-gray-800">
                      {fundraiser.formattedRaisedAmount}
                    </span>
                    <span className="text-sm text-gray-600">
                      {fundraiser.progressPercentage}% of {fundraiser.formattedGoalAmount}
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(fundraiser.progressPercentage || 0, 100)}%` }}
                    ></div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="flex items-center justify-center space-x-1 text-gray-600">
                        <Heart size={16} className="text-red-500" />
                        <span>{fundraiser.supporters}</span>
                      </div>
                      <p className="text-gray-500">Supporters</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-center space-x-1 text-gray-600">
                        <Clock size={16} />
                        <span>{fundraiser.daysLeft || 'No'}</span>
                      </div>
                      <p className="text-gray-500">Days Left</p>
                    </div>
                    
                    <div>
                      <div className="text-gray-600">{fundraiser.lastContributionFormatted}</div>
                      <p className="text-gray-500">Last Donation</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleDonate}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Donate Now via WhatsApp
                  </button>
                  
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Contact Organizer
                  </button>
                </div>

                {fundraiser.isVerified && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-green-500 rounded-full flex-shrink-0"></div>
                      <span className="text-green-800 font-medium">Verified Fundraiser</span>
                    </div>
                    <p className="text-green-700 text-sm mt-1">
                      This fundraiser has been verified by our team
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Story and Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Story */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Patient's Story</h2>
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {fundraiser.story || fundraiser.description}
                  </p>
                </div>
              </div>

              {/* Medical Condition */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Medical Condition</h2>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h3 className="font-semibold text-blue-800">{fundraiser.medicalCondition}</h3>
                  <p className="text-blue-700 mt-2">{fundraiser.description}</p>
                </div>
              </div>

              {/* Documents */}
              {fundraiser.documents && fundraiser.documents.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Medical Documents</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {fundraiser.documents.map((doc, index) => (
                      <a
                        key={index}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                          <span className="text-blue-600 text-sm font-medium">
                            {doc.name.split('.').pop()?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                          <p className="text-xs text-gray-500 capitalize">{doc.type?.replace('_', ' ')}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Updates */}
              {fundraiser.updates && fundraiser.updates.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Updates</h2>
                  <div className="space-y-6">
                    {fundraiser.updates.map((update, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-800">{update.title}</h3>
                          <span className="text-sm text-gray-500">{formatDate(update.date)}</span>
                        </div>
                        <p className="text-gray-700">{update.content}</p>
                        {update.image && (
                          <img
                            src={update.image}
                            alt="Update"
                            className="mt-3 w-full max-w-md h-48 object-cover rounded-lg"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Info */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Info</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Beneficiary:</span>
                    <span className="font-medium capitalize">{fundraiser.beneficiaryRelation?.replace('_', ' ')}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium">{formatDate(fundraiser.createdAt)}</span>
                  </div>
                  
                  {fundraiser.targetDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Target Date:</span>
                      <span className="font-medium">{formatDate(fundraiser.targetDate)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Currency:</span>
                    <span className="font-medium">{fundraiser.currency}</span>
                  </div>
                </div>
              </div>

              {/* Similar Fundraisers */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Other Fundraisers</h3>
                <div className="text-center text-gray-600">
                  <p className="mb-4">Help others in need</p>
                  <button
                    onClick={() => navigate('/all-fundraisers')}
                    className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Browse All Fundraisers
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Contact Information</h3>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone size={20} className="text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">{fundraiser.contactPhone}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail size={20} className="text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{fundraiser.contactEmail}</p>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowContactModal(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <a
                href={`tel:${fundraiser.contactPhone}`}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-center hover:bg-blue-700"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default FundraiserDetail;
